import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ShieldAlert, ShieldCheck, RefreshCw, Loader2, Download, Search, Link2 } from 'lucide-react';
import { disputesAPI } from '../../services/api';
import EfiMedPanel from './EfiMedPanel';
import MpWatchPanel from './disputes/MpWatchPanel';
import DisputesTable from './disputes/DisputesTable';
import { FILTROS, GATEWAY, STATUS, filtrar, baixarCSV, fmtMoeda } from './disputes/disputeUtils';
import { toast } from 'sonner';

const Painel = ({ titulo, valor, extra, tom, testid }) => (
  <div className={`p-3 rounded-lg border bg-white ${tom === 'ruim' ? 'border-red-200' : ''}`}
       data-testid={testid}>
    <p className="text-xs text-gray-500">{titulo}</p>
    <p className={`text-xl font-bold ${tom === 'ruim' ? 'text-red-600' : tom === 'bom' ? 'text-emerald-600' : 'text-gray-700'}`}>
      {valor}
    </p>
    {extra && <p className="text-[11px] text-gray-500">{extra}</p>}
  </div>
);

export const DisputesManagement = () => {
  const [data, setData] = useState({ disputes: [], blocks: [], stats: null });
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);
  const [notes, setNotes] = useState({});
  const [filtros, setFiltros] = useState({ resultado: 'all', gateway: 'all', busca: '' });
  const [vinculando, setVinculando] = useState(null);
  const [resultado, setResultado] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await disputesAPI.list({ limit: 500 });
      setData(r.data || { disputes: [], blocks: [] });
    } catch (e) {
      toast.error('Não foi possível carregar as contestações');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const revincular = async () => {
    setVinculando('run');
    try {
      const r = await disputesAPI.medRematch();
      const n = r.data?.matched ?? 0;
      setResultado(`${n} contestação(ões) vinculada(s) ao pedido · ${r.data?.pending ?? 0} estavam sem vínculo de ${r.data?.total ?? 0} no total.`);
      setVinculando('fim');
      toast.success(n ? `${n} contestação(ões) vinculada(s)` : 'Nenhum pedido novo encontrado');
      await load();
    } catch (e) {
      setVinculando(null);
      toast.error(e.response?.data?.detail || 'Não foi possível vincular os pedidos');
    }
  };

  const agir = async (id, acao) => {
    setActing(`${acao}-${id}`);
    try {
      const note = notes[id] || undefined;
      if (acao === 'release') await disputesAPI.release(id, note);
      if (acao === 'block') await disputesAPI.block(id, note);
      if (acao === 'refresh') {
        const r = await disputesAPI.refresh(id);
        toast[r.data?.ok ? 'success' : 'error'](
          r.data?.ok ? `Status no gateway: ${STATUS[r.data.status]?.label || r.data.status}`
            : (r.data?.message || 'Falha ao reconsultar'));
      }
      if (acao !== 'refresh') toast.success(acao === 'release' ? 'Cliente liberado' : 'Cliente bloqueado');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Ação não concluída');
    } finally {
      setActing(null);
    }
  };

  const stats = data.stats || {};
  const porStatus = stats.by_status || {};
  const valores = stats.amounts || {};
  const dinheiro = (st) => Object.entries(valores[st] || {})
    .map(([m, v]) => fmtMoeda(v, m)).join(' + ');
  const visiveis = useMemo(() => filtrar(data.disputes, filtros), [data.disputes, filtros]);
  const alerta = (porStatus.open || 0) > 0 || (stats.blocked || 0) > 0;

  return (
    <div className="space-y-4" data-testid="disputes-management">
      <Card className={alerta ? 'border-red-400 bg-red-50/60' : 'border-emerald-300 bg-emerald-50/40'}>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center justify-between gap-2 text-base">
            <span className="flex items-center gap-2">
              {alerta ? <ShieldAlert className="h-5 w-5 text-red-600" />
                : <ShieldCheck className="h-5 w-5 text-emerald-600" />}
              Contestações de Pagamento
            </span>
            <span className="flex items-center gap-1">
              <Button variant="outline" size="sm" onClick={revincular} disabled={!!vinculando}
                      title="Procura o pedido de cada MED pelo EndToEndId do Pix e vincula KAKO ID, apelido e pacote"
                      data-testid="disputes-rematch">
                {vinculando ? <Loader2 className="h-4 w-4 animate-spin mr-1" />
                  : <Link2 className="h-4 w-4 mr-1" />}
                Vincular pedidos
              </Button>
              <Button variant="outline" size="sm" onClick={load} disabled={loading}
                      data-testid="disputes-refresh">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              </Button>
            </span>
          </CardTitle>
          {vinculando === 'fim' && (
            <p className="text-xs text-emerald-700" data-testid="disputes-rematch-result">{resultado}</p>
          )}
          <CardDescription>
            Contestação aberta bloqueia o cliente na hora (KAKO ID, documento e WhatsApp).
            <b> Ganhamos</b> = ninguém devolveu dinheiro. <b>Perdemos</b> = o valor voltou pro comprador.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            <Painel titulo="Em aberto" valor={porStatus.open || 0} extra={dinheiro('open')}
                    tom={porStatus.open ? 'ruim' : 'bom'} testid="disputes-count-open" />
            <Painel titulo="Ganhamos" valor={porStatus.closed_won || 0} extra={dinheiro('closed_won')}
                    tom="bom" testid="disputes-count-won" />
            <Painel titulo="Perdemos" valor={porStatus.closed_lost || 0} extra={dinheiro('closed_lost')}
                    tom={porStatus.closed_lost ? 'ruim' : 'bom'} testid="disputes-count-lost" />
            <Painel titulo="Clientes bloqueados" valor={stats.blocked || 0}
                    tom={stats.blocked ? 'ruim' : 'bom'} testid="disputes-count-blocked" />
            <Painel titulo="Histórico total" valor={stats.total || 0} testid="disputes-count-total" />
          </div>
        </CardContent>
      </Card>

      <EfiMedPanel onChanged={load} />
      <MpWatchPanel onChanged={load} />

      <div className="flex flex-wrap items-center gap-2" data-testid="disputes-filters">
        {FILTROS.filter((f) => f.id !== 'closed' || porStatus.closed).map((f) => (
          <Button key={f.id} size="sm" data-testid={`disputes-filter-${f.id}`}
                  variant={filtros.resultado === f.id ? 'default' : 'outline'}
                  onClick={() => setFiltros({ ...filtros, resultado: f.id })}>
            {f.label}
            <span className="ml-1 opacity-70">
              ({f.status ? (porStatus[f.status] || 0) : (stats.total || 0)})
            </span>
          </Button>
        ))}
        <select value={filtros.gateway} data-testid="disputes-filter-gateway"
                onChange={(e) => setFiltros({ ...filtros, gateway: e.target.value })}
                className="h-9 rounded-md border border-gray-300 bg-white px-2 text-sm">
          <option value="all">Todos os gateways</option>
          {Object.entries(GATEWAY).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <div className="relative">
          <Search className="h-4 w-4 absolute left-2 top-2.5 text-gray-400" />
          <Input className="h-9 pl-8 w-56" placeholder="Nome, KAKO ID, documento..."
                 data-testid="disputes-search" value={filtros.busca}
                 onChange={(e) => setFiltros({ ...filtros, busca: e.target.value })} />
        </div>
        <Button size="sm" variant="ghost" onClick={() => baixarCSV(visiveis)}
                disabled={!visiveis.length} data-testid="disputes-export-csv">
          <Download className="h-4 w-4 mr-1" /> Exportar planilha
        </Button>
      </div>

      <DisputesTable disputes={visiveis} agir={agir} acting={acting} notes={notes}
                     setNotes={setNotes} onReload={load} />

      {data.blocks.length > 0 && (
        <Card data-testid="disputes-blocks">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Bloqueios ativos</CardTitle>
            <CardDescription>
              Qualquer uma das três chaves bloqueia: mesmo trocando o KAKO ID, o documento ou o
              WhatsApp repetido continua barrado.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {data.blocks.map((b) => (
              <div key={b.id} className="text-sm p-2 rounded-lg border bg-red-50/50">
                <b>KAKO {b.kako_id || '-'}</b> · doc {b.document || '-'} ·
                WhatsApp ...{b.whatsapp_tail || '-'}
                <span className="block text-xs text-gray-500">
                  desde {new Date(b.created_at).toLocaleString('pt-BR')} ·
                  {' '}{(b.dispute_ids || []).length} contestação(ões)
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DisputesManagement;
